import printJS from 'print-js';

import { presignedURLGet } from 'modules/file_manager/services';
import { deleteFile, downloadAttachments } from 'services/calendarServices';
import { Storage, Auth } from 'aws-amplify';
import Cookies from 'universal-cookie';
import { uuidv4 } from 'common/uuidGenerator';
import { validateUpload } from 'services/fileManager/fileManagerServices';
import { getTenantDetails } from 'utils/utils';
const cookie = new Cookies();

export const UPLOAD_CATEGORY = {
  ROOT: 'root',
  TEMPLATES: 'templates',
  FIRM_DOCUMENTS: 'firm-documents',
  MESSAGES: 'messages',
  EMAILS: 'emails',
  TEXT_MESSAGES: 'text-messages',
  FAXES: 'faxes',
  EVENTS: 'events',
};

export const CASE_FOLDER_NAMES = {
  ROOT: 'root',
  SIGNATURES: 'signatures',
  INTAKE_FORMS: 'intake-forms',
  TIMELINE: 'timeline',
  SUMMARIES: 'summaries',
  DISCREPANCY_REPORTS: 'discrepancy-reports',
};

export function buildS3FilePathV2({
  caseId,
  folderId,
  folderPath,
  fileName,
  category = UPLOAD_CATEGORY.ROOT,
  isTemp = false,
  tempPathId,
  email_message_id,
  text_message_sid,
  event_id,
  message_thread_id,
}) {
  if (!fileName) return '';

  const safe = (s) => (s ? `${s}`.trim() : '');

  const convertEmailId = (str) =>
    str
      .replace(/[<>]/g, '')
      .replace('@', '-at-')
      .replace(/[:"|?*+]/g, '-');

  if (isTemp) {
    return `temp_path/${safe(tempPathId)}/${fileName}`;
  }

  if (category === UPLOAD_CATEGORY.TEMPLATES) {
    return `global/templates/${fileName}`;
  }

  if (category === UPLOAD_CATEGORY.FIRM_DOCUMENTS) {
    let segments = [];
    if (Array.isArray(folderPath)) {
      segments = folderPath.filter(Boolean).map(safe);
    } else if (folderId) {
      segments = [safe(folderId)];
    }

    const folderPrefix = (segments?.length && segments.join('/')) || '';
    return `global/firm-documents/${folderPrefix ? `${folderPrefix}/` : ''}${fileName}`;
  }

  if (category === UPLOAD_CATEGORY.MESSAGES) {
    return `communications/messages/${message_thread_id}/${fileName}`;
  }

  if (category === UPLOAD_CATEGORY.EMAILS) {
    return `communications/emails/${convertEmailId(email_message_id)}/${fileName}`;
  }

  if (category === UPLOAD_CATEGORY.TEXT_MESSAGES) {
    return `communications/text-messages/${text_message_sid}/${fileName}`;
  }

  if (category === UPLOAD_CATEGORY.EVENTS) {
    return `communications/events/${event_id}/${fileName}`;
  }

  if (caseId) {
    let segments = [];

    if (Array.isArray(folderPath)) {
      segments = folderPath.filter(Boolean).map(safe);
    } else if (folderId) {
      segments = [safe(folderId)];
    }

    const folderPrefix = segments.length > 0 ? segments.join('/') : 'root';
    return `cases/${safe(caseId)}/${folderPrefix}/${fileName}`;
  }

  return `global/uploads/${fileName}`;
}

export async function uploadFilesV2(filesInput, options = {}) {
  const {
    caseId,
    folderId,
    folderPath,
    category = UPLOAD_CATEGORY.ROOT,
    isTemp = false,
    userId = '',
    tempSubPath,
    tempPathId,
    progressCallback,
    text_message_sid,
    email_message_id,
    event_id,
    faxId,
    faxDirection,
    message_thread_id,
  } = options;

  const files = Array.isArray(filesInput) ? filesInput : Object.values(filesInput || {});
  const attachments = [];

  const totalSize = files?.reduce((acc, f) => acc + (f?.size || 0), 0) || 0;
  try {
    await validateUpload(totalSize);
  } catch (err) {
    const errorMsg = err?.response?.data?.message || err?.response?.data || err?.message || 'File validation failed';
    throw new Error(`File validation failed: ${errorMsg}`);
  }

  if (!files?.length) return attachments;

  let userAttrs = null;
  try {
    const user = await Auth.currentAuthenticatedUser();
    userAttrs = user?.attributes ?? null;
  } catch (err) {
    console.log('error-getting-user-attributes', err);
  }

  const resolvedUserId = userId || userAttrs?.sub || uuidv4() || '';

  let totalUploaded = 0;

  for (let i = 0; i < files.length; i += 1) {
    const file = files[i];
    const ext = file?.name ? file.name.split('.').pop() : '';
    const uniqueName = `${uuidv4()}${ext ? `.${ext}` : ''}`;
    const filePath = buildS3FilePathV2({
      caseId,
      folderId,
      folderPath,
      tempPathId,
      fileName: uniqueName,
      category,
      isTemp,
      userId: resolvedUserId,
      tempSubPath,
      email_message_id,
      text_message_sid,
      event_id,
      faxId,
      faxDirection,
      message_thread_id,
    });

    const [tenantId] = getTenantDetails();
    const env = process.env.REACT_APP_ENV === 'dev' ? 'develop' : process.env.REACT_APP_ENV;
    const bucket = `lawft-documents-${tenantId}-${env}`;
    Storage.configure({
      bucket,
      level: 'public',
      region: process.env.REACT_APP_REGION,
      identityPoolId: process.env.REACT_APP_IDP_ID,
      customPrefix: { public: '' },
    });

    await Storage.put(filePath, file, {
      contentType: file?.type || 'application/octet-stream',
      progressCallback: (progress) => {
        if (progressCallback && totalSize) {
          const { loaded, total } = progress;
          const currentFileLoaded = total ? loaded : 0;
          const overall = Math.min(100, Math.floor(((totalUploaded + currentFileLoaded) / totalSize) * 100));
          progressCallback(overall);
        }
      },
    });

    totalUploaded += file?.size || 0;
    const storedPath = filePath;
    attachments.push({
      file_name: file?.name,
      display_name: file?.name,
      size: String(file?.size ?? ''),
      file_path: storedPath,
      file_type: (file?.type || '').toString(),
      created_by: userAttrs,
      isFileUploaded: true,
    });
  }

  if (progressCallback) progressCallback(100);
  return attachments;
}

// to print pdf
export const pdfTronPrintFile = async (file) => {
  try {
    let extension = file.display_name.split('.').slice(-1)[0].toLowerCase(); //it may be case sensitive sometimes so used the lowercase
    let response = await presignedURLGet(file.file_path, file.version_id);

    if (extension === 'pdf') {
      fetch(response.data?.file_url)
        .then((res) => res.blob()) // Gets the response and returns it as a blob
        .then((blob) => {
          const blobUrl = URL.createObjectURL(blob);
          // Use printJS immediately without any delay
          printJS(blobUrl);
        })
        .catch((error) => {
          console.error('Error printing PDF:', error);
          // Fallback: open in new tab and trigger print immediately
          const newWindow = window.open(response.data?.file_url, '_blank');
          if (newWindow) {
            newWindow.onload = function () {
              newWindow.print(); // No delay - immediate print
            };
          }
        });
    } else {
      // Check if WebViewer/PDFNet is available
      if (window?.Core && window?.PDFNet) {
        window?.Core?.setWorkerPath('webviewer/core');
        await window?.PDFNet?.initialize();
        const options = {
          // l: licenseKey,
          extension,
        };

        // perform the conversion with no optional parameters
        const buf = await window?.Core?.officeToPDFBuffer(response.data?.file_url, options);
        const blob = new Blob([buf], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        // Use printJS immediately without delay
        printJS(blobUrl);
      } else {
        // Fallback: open file directly in new tab and trigger print immediately
        console.warn('WebViewer/PDFNet not available, opening file in new tab and triggering print');
        const newWindow = window.open(response.data?.file_url, '_blank');
        if (newWindow) {
          newWindow.onload = function () {
            newWindow.print(); // No delay - immediate print
          };
        }
      }
    }
  } catch (error) {
    console.error('Print function failed:', error);
    // Final fallback: try to open file directly and trigger print immediately
    if (file?.file_path) {
      const newWindow = window.open(file.file_path, '_blank');
      if (newWindow) {
        newWindow.onload = function () {
          newWindow.print(); // No delay - immediate print
        };
      }
    }
  }
};

// to download a file
export const downloadFile = async (file) => {
  downloadAttachments(file)
    .then((data) => {
      let link = document.createElement('a');
      link.download = file.display_name;
      let url = window.URL.createObjectURL(data.Body);
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    })
    .catch((err) => {
      console.log('error-downloading-attachment', err);
    });
};

/** --- Function to delete attachment from S3 bucket --- **/
export const deleteAttachment = async (file) => {
  deleteFile(file).catch((err) => {
    console.log('error-deleting-attachment', err);
  });
};

export async function uploadFiles(filesObj, module, case_id = '', userId = '', progressCallBack) {
  const files = Object.values(filesObj || {});
  if (!files?.length) return [];
  return uploadFilesV2(files, {
    caseId: case_id || undefined,
    category: UPLOAD_CATEGORY.ROOT,
    userId: userId || undefined,
    progressCallback: progressCallBack || undefined,
  });
}

export function getFileType(type) {
  switch (type.toLowerCase()) {
    case 'pdf':
      return 'pdf';
    case 'docx':
    case 'doc':
    case 'rtf':
    case 'ods':
      return 'doc';
    case 'png':
    case 'bmp':
    case 'tif':
    case 'gif':
    case 'webp':
    case 'jpg':
    case 'jpeg':
    case 'heic':
    case 'heif':
      return 'image';
    case 'mp4':
    case 'mpg':
    case '3gp':
    case 'wmv':
    case 'avi':
    case 'mkv':
    case 'mov':
    case 'ogg':
    case 'wav':
    case 'mp3':
    case 'wma':
    case 'aac':
    case 'm4a':
      return 'video';
    case 'txt':
      return 'text';
    case 'xlsx':
    case 'xls':
    case 'csv':
      return 'xls';
    default:
      return 'folder';
  }
}

export function getFileExtension(fileName) {
  if (fileName) {
    const lastIndex = fileName?.lastIndexOf('.');
    if (lastIndex === -1) {
      return '';
    } else {
      return fileName.substring(lastIndex + 1);
    }
  }
  return '';
}

export const loadIcon = (format) => {
  switch (format?.toLowerCase()) {
    case 'pdf':
      return <i className="bi bi-file-earmark-pdf-fill"></i>;
    case 'docx':
    case 'doc':
    case 'rtf':
    case 'ods':
      return <i className="bi bi-file-earmark-word-fill"></i>;
    case 'xlsx':
    case 'csv':
    case 'xls':
      return <i className="bi bi-file-earmark-excel-fill"></i>;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'heic':
    case 'heif':
      return <i className="bi bi-file-earmark-image-fill"></i>;
    case 'mp4':
    case 'avi':
    case 'mkv':
    case 'mov':
    case 'ogg':
    case 'mpg':
    case '3gp':
    case 'wmv':
      return <i className="bi bi-file-earmark-play-fill"></i>;
    case 'wav':
    case 'mp3':
    case 'wma':
    case 'aac':
      return <i className="bi bi-file-earmark-music-fill"></i>;
    case 'txt':
      return <i className="bi bi-file-earmark-text-fill"></i>;
    default:
      return <i className="fas fa-folder"></i>;
  }
};
